// The active month: the month the household works in, separate from the calendar month. It moves
// only when the person starts the next month ("Start your November budget"); the cron never moves
// it. Copy and pure rules shared by the Worker and the PWA (src/services/months.ts, the Overview's
// new month card, the month list and the "Viewing October" banner).

const NAMES = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

/** "2026-11" -> "November". */
export const monthLong = (key: string) => NAMES[Number(key.slice(5, 7)) - 1] ?? key;
/** "2026-11" -> "November 2026" (the month list). */
export const monthWithYear = (key: string) => `${monthLong(key)} ${key.slice(0, 4)}`;

/** The month after `key` ("2026-12" -> "2027-01"). */
export function nextMonthKey(key: string): string {
  const y = Number(key.slice(0, 4));
  const m = Number(key.slice(5, 7));
  return m === 12 ? `${y + 1}-01` : `${y}-${String(m + 1).padStart(2, '0')}`;
}
export function prevMonthKey(key: string): string {
  const y = Number(key.slice(0, 4));
  const m = Number(key.slice(5, 7));
  return m === 1 ? `${y - 1}-12` : `${y}-${String(m - 1).padStart(2, '0')}`;
}

/** The active month as read: the stored one, never ahead of the calendar month; none stored is the calendar month. */
export const effectiveActive = (stored: string | null | undefined, calendar: string) => (stored && stored < calendar ? stored : calendar);

/** From the 7th of the calendar month (or once two months behind), the card adds a gentle line. Never an auto-start. */
export const NUDGE_DAY = 7;
export const nudgeDue = (calendarDay: string, active: string) => {
  const calendar = calendarDay.slice(0, 7);
  return nextMonthKey(active) < calendar || Number(calendarDay.slice(8, 10)) >= NUDGE_DAY;
};

/** The new month card. */
export const NEW_MONTH_TITLE = (calendar: string) => `It's ${monthLong(calendar)}.`;
export const NEW_MONTH_BODY = (next: string) => `Start your ${monthLong(next)} budget.`;
export const NEW_MONTH_BUTTON = (next: string) => `Start ${monthLong(next)}`;
export const NEW_MONTH_WAITING = (n: number, next: string) => `${n} ${n === 1 ? 'purchase is' : 'purchases are'} already waiting for ${monthLong(next)}.`;
export const NEW_MONTH_NUDGE = (active: string, next: string) => `${monthLong(next)} is a week in. Start it when ${monthLong(active)} is done; ${monthLong(active)} stays open either way.`;

/** On a month that is not the active one. */
export const VIEWING = (month: string) => `Viewing ${monthLong(month)}`;
export const BACK_TO = (month: string) => `Back to ${monthLong(month)}`;
/** Under it: where a planned amount edited here goes (src/services/month-plans.ts). */
export const PLAN_ONLY_HERE = (month: string) => `Changes here only affect ${monthLong(month)}.`;
export const PLAN_EVERY_MONTH = 'Planned amounts changed here change every month.';

/** The add sheet, on a month other than the calendar month: where a hand-added purchase goes. */
export const ADD_INTO = (month: string) => `This purchase goes into ${monthLong(month)}.`;

/** The Accept sheet: the month the share counts in (the month of its date). */
export const SHARE_COUNTS_IN = (date: string) => `It counts in ${monthLong(date.slice(0, 7))}.`;

/** The toast after starting a month. */
export const MONTH_STARTED = (month: string) => `${monthLong(month)} started. Your earlier months stay open.`;
